import { COOKIE_ECHO_USER } from '../constants/EarlyRequesterNames';
import { getPublicCurrencyV2Rpc, getPublicFeatureFlagsV2Rpc, getPublicProductGroupsRpc, getPublicProductsV2Rpc } from 'pricing-pages-ui-public-app/__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc';
import { handleEarlyRequestError, normaliseCurrencyRequest } from '../utils/earlyRequest';
import { getProductGroupBySlug } from '../utils/getProductGroupBySlug';
import { isCountryCodeString } from '../utils/currencyTypes';
import { getBrowserLocale, getCountryCode } from '../utils/getPreferredLanguage';
import { setHubspotUtk } from '../utils/windowGlobals';
import { getIsQAProxy } from 'pricing-pages-lib/utils/proxyUtils';
import { getBooleanFromString, getQueryParam, getShortLinkForRedirect, getUtk } from 'pricing-pages-ui/earlyRequester/earlyRequesterUtils';
import { laboratoryClient } from 'pricing-pages-ui/utils/laboratoryClient';
import { KNOWN_PRODUCT_API_NAMES } from '../constants/allProductApiNames';
import quickFetch from 'quick-fetch';
import { makeChirpEarlyRequest } from 'rpc-client-utils';

/* Best-guess currency for the early product-groups request, keyed on the
   locale country the per-locale HTML wrapper injects (COUNTRY_CODE_FROM_LOCALE
   / .in domain). Prefetching in USD reflows non-US locales to their real
   currency once getPublicCurrencyV2 resolves; guessing from the locale avoids
   that USD flash and raises the early-request cache hit rate. The real query
   still refetches if the server resolves a different currency. */
const FALLBACK_CURRENCY_BY_COUNTRY = {
  US: 'USD',
  IN: 'INR',
  DE: 'EUR',
  FR: 'EUR',
  JP: 'JPY',
  GB: 'GBP',
  AU: 'AUD',
  SG: 'SGD',
  CA: 'CAD',
  CO: 'COP'
};
(function () {
  // getBrowserLocale reads the debug override (PUBLIC_DEBUG_BROWSER_LOCALE),
  // falls back to navigator language, always lowercased so the hash matches
  // what DFC queries compute when reading the cached result.
  const browserLocale = getBrowserLocale();
  const isQAProxy = getIsQAProxy();
  const utk = getUtk() || '';
  setHubspotUtk(utk); // global var so we can access in the app code

  const cookieUrl = isQAProxy ? /* eslint-disable-next-line hubspot-dev/prefer-hubspot-url-utils */
  'https://api.hubspotqa.com/cookie-echo/v1/user' : quickFetch.getApiUrl('/cookie-echo/v1/user', false);
  // Redirect detection
  quickFetch.makeEarlyRequest(COOKIE_ECHO_USER, {
    url: cookieUrl,
    type: 'GET',
    dataType: 'json',
    // Relatively quick timeout since missing the redirect is not a big deal, and we don't want to block pageload long
    // This might mean that redirects skew towards NorthAm and those with fast internet connections
    timeout: 2000,
    whenFinished(response) {
      // This endpoint will 401 if you're not logged in so wont hit this block
      const email = response.email;

      // by default we don't redirect hubspot users. We can override that by having hsRedirect=true in query param
      const hsRedirect = getQueryParam('hsRedirect');
      const shouldRedirectHubSpotUsers = getBooleanFromString(hsRedirect || '');
      if (email.indexOf('@hubspot.com') > -1 && !shouldRedirectHubSpotUsers) {
        // Don't redirect hubspot users. Important for sales
        // who want a consistent demo experience on hubspot.com
        return;
      }

      // Use redirect so we don't keep the public page in history
      const redirectUrl = getShortLinkForRedirect(window.location.href);
      window.location.replace(redirectUrl);
    }
  });
  const preferredCountryCode = getCountryCode();
  const normalizedCountryCode = isCountryCodeString(preferredCountryCode) ? preferredCountryCode : undefined;
  void makeChirpEarlyRequest(Object.assign({}, getPublicFeatureFlagsV2Rpc, {
    earlyRequest: true
  }), {
    request: {
      utk,
      browserLocale,
      // Normalise to match what fetchPublicFeatureFlags sends
      preferredCountryCode: normalizedCountryCode
    }
  }).catch(handleEarlyRequestError);

  // @ts-expect-error mock is only set in unit tests
  if (!quickFetch.mockRequests) {
    laboratoryClient.resolve();
  }

  // Currency endpoint — normalise inputs before CHIRP call so the hash computed
  // here matches the hash the DFC query computes when reading the cached result.
  void makeChirpEarlyRequest(Object.assign({}, getPublicCurrencyV2Rpc, {
    earlyRequest: true
  }), {
    request: normaliseCurrencyRequest({
      browserLocale,
      preferredCountryCode
    })
  }).catch(handleEarlyRequestError);
  void makeChirpEarlyRequest(Object.assign({}, getPublicProductsV2Rpc, {
    earlyRequest: true
  }), {
    request: {
      productApiNames: KNOWN_PRODUCT_API_NAMES
    }
  }).catch(handleEarlyRequestError);

  // Product groups — use the URL currencyCode param when present so the
  // early request matches what CurrencyContext will resolve to. Users who
  // arrive via shared links or the currency picker already have the param;
  // for everyone else we guess from the locale country (falling back to USD)
  // to avoid prefetching in USD and reflowing once the real currency resolves.
  // The hook in usePricingPageData skips until CurrencyContext resolves so the
  // first real query fires with the validated displayed currency and hits this
  // cache.
  const pageSlug = window.location.pathname.split('/')[2] || '';
  const productGroup = getProductGroupBySlug(pageSlug);
  if (productGroup) {
    const urlCurrency = new URLSearchParams(window.location.search).get('currencyCode');
    // Reject obviously invalid values so we don't fire an RPC with garbage
    // and miss the cache when CurrencyContext falls back to the server default.
    const isPlausibleCurrency = urlCurrency !== null && urlCurrency !== 'UNDEFINED' && /^[A-Z]{2,3}$/.test(urlCurrency);
    const localeFallbackCurrency = normalizedCountryCode && FALLBACK_CURRENCY_BY_COUNTRY[normalizedCountryCode] || 'USD';
    const currencyCode = isPlausibleCurrency ? urlCurrency : localeFallbackCurrency;
    void makeChirpEarlyRequest(Object.assign({}, getPublicProductGroupsRpc, {
      earlyRequest: true
    }), {
      request: {
        productGroup,
        currencyCode,
        preferredCountry: normalizedCountryCode,
        browserLocale
      }
    }).catch(handleEarlyRequestError);
  }
})();